import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Input, NativeDatePicker, Select, Text } from "components/base/Form";
import Page from "components/base/Page";
import { Alert } from "components/base/Toast";
import OdooStyleProductSelector from "components/shared/OdooStyleProductSelector";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import { useContext, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import CreateShopifyAdjustmentView from "./CreateShopifyAdjustmentView";
import formatShipmentProductName from "./formatShipmentProductName";
import {
  DELETE_STOCK_SHIPMENT,
  FETCH_STOCK_SHIPMENTS,
  SAVE_STOCK_SHIPMENT,
} from "./graphql";
import ShopifyInventoryView from "./ShopifyInventoryView";
import SyncStockShipmentToOdooView from "./SyncStockShipmentToOdooView";

const MOVE_TYPES = [
  { value: "OUT", label: "Out" },
  { value: "IN", label: "In" },
  { value: "TRANSFER", label: "Transfer" },
];
const SHIPMENT_STATES = ["DRAFT", "RESERVED", "CONFIRMED", "DONE", "CANCELLED"];
const REF_TYPE_OPTIONS = [
  { value: "WIS_EDIT_RECORD", label: "Manual Edit Record" },
  { value: "AMAZON_PO", label: "Amazon PO" },
  { value: "WIS_SALES_ORDER", label: "WIS - Sales Order" },
  { value: "RETAILER_ORDER", label: "Retailer Order" },
  { value: "SHOPIFY_ORDER", label: "Shopify Order" },
];

const DEFAULT_VALUES = {
  moveType: "OUT",
  date: new Date().toISOString().slice(0, 10),
  toLocationId: "0",
  toLocationType: "STOCK_CATEGORY",
  notes: "",
  refId: "",
  refType: "WIS_EDIT_RECORD",
  refName: "",
  state: "CONFIRMED",
  lines: [],
};

const StockShipmentFormPage = ({
  mode = "create",
  stockCategoryId: stockCategoryIdProp,
  originalShipment = null,
  warehouseName = "",
  productStocks = {},
  transferToOptions = [],
}) => {
  const isEditMode = mode === "edit";
  const navigate = useNavigate();
  const { odooUrl } = useContext(OdooContext);
  const { stockCategoryId: stockCategoryIdFromParams } = useParams();
  const stockCategoryId = stockCategoryIdProp || stockCategoryIdFromParams;
  const shipmentId = originalShipment?.id || null;
  const shipmentInitialValues = originalShipment
    ? {
        ...DEFAULT_VALUES,
        moveType: originalShipment.moveType || DEFAULT_VALUES.moveType,
        date: originalShipment.date || DEFAULT_VALUES.date,
        toLocationId:
          originalShipment.toLocationId || DEFAULT_VALUES.toLocationId,
        toLocationType:
          originalShipment.toLocationType || DEFAULT_VALUES.toLocationType,
        notes: originalShipment.notes || DEFAULT_VALUES.notes,
        refId: originalShipment.refId || DEFAULT_VALUES.refId,
        refType: originalShipment.refType || DEFAULT_VALUES.refType,
        refName: originalShipment.refName || DEFAULT_VALUES.refName,
        state: originalShipment.state || DEFAULT_VALUES.state,
        lines:
          originalShipment.lines && originalShipment.lines.length > 0
            ? originalShipment.lines.map((line) => ({
                id: line.id,
                productId: line.product?.id || "",
                productNumber: line.product?.number || "",
                productName: line.product?.name || "",
                countryCodeOfOrigin: line.product?.countryCodeOfOrigin || "",
                version: line.product?.version || "",
                itemsPerSet: line.product?.itemsPerSet ?? null,
                qty: line.qty ?? "",
                name: formatShipmentProductName(line.product),
              }))
            : DEFAULT_VALUES.lines,
      }
    : DEFAULT_VALUES;

  const [moveType, setmoveType] = useState(shipmentInitialValues.moveType);
  const [date, setDate] = useState(shipmentInitialValues.date);
  const [toLocationId, setToLocationId] = useState(
    shipmentInitialValues.toLocationId,
  );
  const [toLocationType] = useState(
    shipmentInitialValues.toLocationType || "STOCK_CATEGORY",
  );
  const [notes, setNotes] = useState(shipmentInitialValues.notes);
  const [refId, setRefId] = useState(shipmentInitialValues.refId);
  const [refType, setRefType] = useState(shipmentInitialValues.refType);
  const [refName, setRefName] = useState(shipmentInitialValues.refName);
  const [state, setState] = useState(shipmentInitialValues.state);
  const [lines, setLines] = useState(shipmentInitialValues.lines);
  const filteredTransferToOptions = transferToOptions.filter(
    (category) => `${category.id}` !== `${stockCategoryId}`,
  );

  const [saveStockShipment, saveStockShipmentRes] = useMutation(
    SAVE_STOCK_SHIPMENT,
    {
      onCompleted(res) {
        const savedId = res?.saveStockShipment?.shipment?.id;
        Alert(
          "success",
          isEditMode ? "Stock record saved." : "Stock record created.",
        );
        if (!isEditMode && savedId) {
          navigate(`/sales/inventory/${stockCategoryId}/shipments/${savedId}`);
        }
      },
      onError(error) {
        window.alert(parseError(error));
      },
      refetchQueries: ["FETCH_STOCK_SHIPMENT_FORM_DATA"],
      awaitRefetchQueries: true,
    },
  );

  const [deleteStockShipment, deleteStockShipmentRes] = useMutation(
    DELETE_STOCK_SHIPMENT,
    {
      onCompleted() {
        Alert("success", "Stock record deleted.");
        navigate(`/sales/inventory/${stockCategoryId}/shipments`);
      },
      onError(error) {
        window.alert(parseError(error));
      },
      refetchQueries: [
        {
          query: FETCH_STOCK_SHIPMENTS,
          variables: { warehouseId: stockCategoryId },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  const modal = useModals();
  const shopifyInventoryModal = useModals();

  function addLine() {
    setLines((prev) => [
      ...prev,
      { productId: "", qty: "", itemsPerSet: null, isNew: true },
    ]);
  }

  function isLineQtyValid(line) {
    const itemsPerSet = parseInt(line?.itemsPerSet, 10);
    const qty = parseInt(line?.qty, 10);
    if (!line?.productId) return true;
    if (Number.isNaN(itemsPerSet) || itemsPerSet <= 0) return true;
    if (Number.isNaN(qty)) return true;
    return qty % itemsPerSet === 0;
  }

  function buildLineProductView(line) {
    if (!line?.productId) return null;
    const product = {
      number: line.productNumber,
      name: line.productName,
      countryCodeOfOrigin: line.countryCodeOfOrigin,
      version: line.version,
      itemsPerSet: line.itemsPerSet,
    };
    const text = formatShipmentProductName(product);
    if (!text) return null;
    return <div>{text}</div>;
  }

  function selectLineProduct(index, product) {
    if (!product?.id) return;

    const hasDuplicate = lines.some(
      (line, lineIndex) =>
        lineIndex !== index && `${line.productId}` === `${product.id}`,
    );
    if (hasDuplicate) {
      Alert("error", "This product is already selected in another line.");
      return;
    }

    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        return {
          ...line,
          productId: product.id,
          productNumber: product.number || "",
          productName: product.name || "",
          countryCodeOfOrigin: product.countryCodeOfOrigin || "",
          version: product.version || "",
          itemsPerSet: product.itemsPerSet ?? null,
          name: formatShipmentProductName(product),
          isNew: false,
        };
      }),
    );
  }

  function updateLine(index, values) {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        return { ...line, ...values };
      }),
    );
  }

  function removeLine(index) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  function handleLineSelectorBlur(index) {
    setTimeout(() => {
      setLines((prev) => {
        const line = prev[index];
        if (!line || line.productId) return prev;
        return prev.filter((_, i) => i !== index);
      });
    }, 0);
  }

  function saveHandler() {
    if (!stockCategoryId) {
      window.alert("Warehouse is required.");
      return;
    }
    if (!date) {
      window.alert("Date is required.");
      return;
    }
    if (!refType) {
      window.alert("Ref Type is required.");
      return;
    }
    if (moveType === "TRANSFER" && (!toLocationId || toLocationId === "0")) {
      window.alert("Transfer to is required for transfer move type.");
      return;
    }
    if (moveType === "TRANSFER" && `${toLocationId}` === `${stockCategoryId}`) {
      window.alert("Transfer to cannot be the current warehouse.");
      return;
    }

    const validLines = lines
      .filter((line) => line.productId)
      .map((line) => {
        const qty = parseInt(line.qty, 10);
        return { productId: line.productId, qty };
      });

    if (validLines.length === 0) {
      window.alert("Please add at least one product line.");
      return;
    }

    if (validLines.some((line) => !line.productId || Number.isNaN(line.qty))) {
      window.alert("Each line must have a product and integer qty.");
      return;
    }

    saveStockShipment({
      variables: {
        ...(isEditMode && shipmentId ? { id: shipmentId } : {}),
        moveType: moveType,
        toLocationId: moveType === "TRANSFER" ? toLocationId : null,
        toLocationType: moveType === "TRANSFER" ? toLocationType : null,
        date,
        refId: refId?.trim() || null,
        refType,
        refName: refName?.trim() || null,
        state,
        notes: notes?.trim() || "",
        warehouseId: `${stockCategoryId}`,
        warehouseName: warehouseName || "",
        warehouseType: "STOCK_CATEGORY",
        lines: validLines,
      },
    });
  }

  function deleteHandler() {
    if (!shipmentId || !isEditMode) return;
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    deleteStockShipment({ variables: { id: shipmentId } });
  }

  function createOdooInternalTransferHandler() {
    modal.present({
      title: "Create Internal Transfer in Odoo",
      maxWidth: "max-w-5xl",
      children: (
        <SyncStockShipmentToOdooView
          shipment={originalShipment}
          onCompleted={modal.hide}
        />
      ),
    });
  }

  function viewShopifyInventoryHandler() {
    shopifyInventoryModal.present({
      title: "Shopify Current Inventory",
      children: (
        <ShopifyInventoryView
          shipment={originalShipment}
          productStocks={productStocks}
        />
      ),
    });
  }

  function createShopifyAdjustmentHandler() {
    modal.present({
      title: "Create Adjustment in Shopify",
      maxWidth: "max-w-5xl",
      children: (
        <CreateShopifyAdjustmentView
          shipment={originalShipment}
          productStocks={productStocks}
          onCompleted={didUpdateShopifyInventoryHandler}
        />
      ),
    });
  }

  function didUpdateShopifyInventoryHandler() {
    modal.hide();
    viewShopifyInventoryHandler();
  }

  return (
    <Page
      title={
        isEditMode ? `Stock Record #${shipmentId || ""}` : "Create Stock Record"
      }
      subtitle={warehouseName}
      backTo="./.."
      className="h-screen"
      rightButtons={
        <div className="flex items-center space-x-6">
          {shipmentId && (
            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL}stock/shipment/${
                shipmentId
              }`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Admin
            </a>
          )}
          <Button
            primary
            loading={saveStockShipmentRes.loading}
            onClick={saveHandler}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="p-6 h-full flex flex-col">
        <div className="grid gap-6 grid-cols-12 flex-1 min-h-0">
          <div className="col-span-8 space-y-6 min-h-0">
            <div className="card">
              <h5>Base Info</h5>
              <div className="mt-4 text-sm -mx-2">
                <table>
                  <tbody>
                    <tr>
                      <td className="w-32">
                        <label>Move Type:</label>
                      </td>
                      <td>
                        <div className="flex items-center space-x-6">
                          {MOVE_TYPES.map((type) => (
                            <label
                              key={type.value}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="moveType"
                                value={type.value}
                                checked={moveType === type.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setmoveType(value);
                                  if (value === "TRANSFER") {
                                    setRefType("WIS_EDIT_RECORD");
                                  }
                                }}
                              />
                              <span>{type.label}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>

                    {moveType === "TRANSFER" ? (
                      <tr>
                        <td>
                          <label>Transfer to:</label>
                        </td>
                        <td>
                          <Select
                            className="w-full"
                            value={toLocationId}
                            onChange={(e) => setToLocationId(e.target.value)}
                          >
                            <option value="0">--- Select To ---</option>
                            {filteredTransferToOptions.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </Select>
                        </td>
                      </tr>
                    ) : null}

                    <tr>
                      <td>
                        <label>Date:</label>
                      </td>
                      <td>
                        <NativeDatePicker
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                        />
                      </td>
                    </tr>

                    {moveType !== "TRANSFER" && (
                      <>
                        <tr>
                          <td>
                            <label>Ref Type:</label>
                          </td>
                          <td>
                            <Select
                              disabled={moveType === "TRANSFER"}
                              className="w-full"
                              value={refType}
                              onChange={(e) => setRefType(e.target.value)}
                            >
                              {REF_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <label>Ref ID:</label>
                          </td>
                          <td>
                            <Input
                              value={refId}
                              onChange={(e) => setRefId(e.target.value)}
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <label>Ref Note:</label>
                          </td>
                          <td>
                            <Input
                              className="w-full"
                              value={refName}
                              onChange={(e) => setRefName(e.target.value)}
                            />
                          </td>
                        </tr>
                      </>
                    )}

                    <tr>
                      <td className="align-top pt-2">
                        <label>Note:</label>
                      </td>
                      <td>
                        <Text
                          placeholder="Write notes for this inventory record"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card p-0 flex-1 flex flex-col">
              <div className="p-6 pb-0">
                <h5>Products</h5>
              </div>

              {lines.length > 0 && (
                <div className="mt-4 flex-1 relative">
                  <table>
                    <thead>
                      <tr className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700/30 bg-opacity-80 whitespace-nowrap">
                        <th className="px-6">Product</th>
                        <th className="px-3">Available</th>
                        <th className="px-6 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <tr key={index}>
                          <td className="py-2 px-6 w-full">
                            <div className="flex items-center space-x-2">
                              <Button danger onClick={() => removeLine(index)}>
                                <BsTrash />
                              </Button>
                              <div className="w-full">
                                <OdooStyleProductSelector
                                  isActiveOnly={false}
                                  isSalable={null}
                                  valueView={buildLineProductView(line)}
                                  autoFocus={line.isNew && !line.productId}
                                  onSelect={(product) =>
                                    selectLineProduct(index, product)
                                  }
                                  onBlur={() => handleLineSelectorBlur(index)}
                                  optionView={(product) =>
                                    formatShipmentProductName(
                                      product,
                                      productStocks[product.id],
                                    )
                                  }
                                  placeholder="Search by product number or name"
                                  preferStockCategoryId={stockCategoryId}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-right">
                              {productStocks[line.productId] ?? "-"}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              validated={isLineQtyValid(line)}
                              className="text-right"
                              value={line.qty}
                              onChange={(e) =>
                                updateLine(index, { qty: e.target.value })
                              }
                              type="number"
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="p-6">
                <Button onClick={addLine}>+ Product</Button>
              </div>
            </div>
          </div>

          <div className="col-span-4 space-y-6">
            <div className="card">
              <h5>State</h5>
              <div className="mt-2 text-xs opacity-70">
                Default state is CONFIRMED. This state will be used to compute
                the stock consumption and availability. DRAFT and CANCELLED
                states will not affect the stock.
              </div>
              <div className="mt-4 text-sm">
                <Select
                  className="-ml-0.5"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  {SHIPMENT_STATES.map((stateValue) => (
                    <option key={stateValue} value={stateValue}>
                      {stateValue}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {moveType === "TRANSFER" && originalShipment && (
              <div className="card">
                <h5>Odoo</h5>

                <div className="mt-2 text-xs opacity-70">
                  You can create an internal transfer in Odoo for this record.
                  The linked transfter will be shown below once created. You can
                  also click the link to view the transfer in Odoo.
                </div>

                <div className="mt-2 space-y-2">
                  {originalShipment.odooStockPickingId ? (
                    <div>
                      <a
                        data-odoo
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${odooUrl}/odoo/action-1058/${originalShipment.odooStockPickingId}?cids=${originalShipment.odooCompanyId}`}
                      >
                        {originalShipment.odooStockPickingName ||
                          `Transfer #${originalShipment.odooStockPickingId}`}
                      </a>
                    </div>
                  ) : null}

                  <Button onClick={createOdooInternalTransferHandler}>
                    Create Internal Transfer in Odoo
                  </Button>
                </div>
              </div>
            )}

            {moveType === "TRANSFER" && originalShipment && (
              <div className="card">
                <h5>Shopify</h5>

                <div className="mt-2 text-xs opacity-70">
                  You can create an adjustment in Shopify for this record.
                </div>

                <div className="mt-2 space-y-2">
                  <Button onClick={viewShopifyInventoryHandler}>
                    View Shopify Current Inventory
                  </Button>

                  <Button onClick={createShopifyAdjustmentHandler}>
                    Create adjustment in Shopify
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              {isEditMode ? (
                <Button
                  danger
                  loading={deleteStockShipmentRes.loading}
                  onClick={deleteHandler}
                >
                  Delete Record
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default StockShipmentFormPage;
